/**
 * Change Indicator Checklist Tab (Tab 4)
 * 
 * Centralized dashboard for reviewing process owner inputs from EX7
 * Features:
 * - Table 1: Change Indicators - Aggregated checklist responses
 * - Table 2: Certification Messages - Comments from PO (Admin only)
 * - Mark as Read / Delete actions
 * - Collapsible sections
 * - API integration
 * 
 * Note: Table 2 is only visible to Admin users
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Table, Dropdown, message, Spin, Tooltip } from 'antd';
import type { MenuProps, TableColumnsType } from 'antd';
import { MoreOutlined, CheckOutlined, DeleteOutlined, DownOutlined, RightOutlined, LoadingOutlined } from '@ant-design/icons';
import ExportButton from '../../../../components/common/ExportButton';
import styled from 'styled-components';
import ExportPanel from '../../../../components/common/ExportPanel';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../../store';
import { setChangeIndicators, setCertificationMessages } from '../../../../store/slices/auditPlanSlice';
import ConfirmModal from '../../../../components/common/ConfirmModal';
import trashIcon from '../../../../assets/icons/ic-trash.svg';
import RightContentDrawer from '../../../../components/common/RightContentDrawer';
import {
  getChangeIndicators,
  getCertificationMessages,
  markChangeIndicatorAsRead,
  markCertificationMessageAsRead,
  deleteChangeIndicator as deleteChangeIndicatorApi,
  deleteCertificationMessage as deleteCertificationMessageApi,
} from '../../../../services/auditPlanService';
import { useNavigate } from 'react-router-dom';

// ========== TYPE DEFINITIONS ==========

export type ChangeIndicatorStatus = 'pending' | 'read';
export type SignificantChangeValue = 'changeIdentified' | 'noChanges' | 'na';

export interface ChangeIndicator {
  id: string;
  quarter: string;
  processOwnerName: string;
  processOwnerAvatar?: string;
  question: string;
  response: string;
  significantChange: SignificantChangeValue;
  status: ChangeIndicatorStatus;
  processes: Array<{ id: string; name: string }>;
  createdAt: string;
}

export interface CertificationMessage {
  id: string;
  quarter: string;
  processOwnerName: string;
  processOwnerAvatar?: string;
  section: string;
  message: string;
  status: ChangeIndicatorStatus;
  processes: Array<{ id: string; name: string }>;
  createdAt: string;
}

interface ChangeIndicatorChecklistTabProps {
  isAdmin: boolean;
}


// ========== STYLED COMPONENTS ==========

// Section colors from AdminDashboardTab
const SECTION_COLORS = {
  changeIndicators: '#E3F2FD', // Blue 50
  certificationMessages: '#F3E5F5', // Purple 50
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.div<{ $bgColor: string }>`
  background: ${props => props.$bgColor};
  padding: 16px 2px 2px 2px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: visible;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  margin-left: 16px;
  margin-right: 8px;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.8;
  }
`;

const CollapseIcon = styled.div`
  display: flex;
  align-items: center;
  color: #6B7280;
  transition: transform 0.2s;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SectionTitle = styled.span`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  color: #1F2937;
  flex: 1;
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  font-family: Inter;
  font-weight: 600;
  font-size: 12px;
  color: #2563EB;
  margin-right: 16px;
`;

const GridWrapper = styled.div`
  background: white;
  border-radius: 6px;
`;

const TableWrapper = styled.div`
  .ant-table {
    border-radius: 0;
  }

  .ant-table-thead > tr > th {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 12px;
    color: #6B7280;
    padding: 12px 16px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    
    &:first-child {
      border-left: none;
    }
    
    /* Remove Ant Design's internal column separators */
    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    font-family: Inter;
    font-size: 14px;
    color: #1F2937;
    padding: 16px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    
    &:first-child {
      border-left: none;
    }
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }

  .ant-table-tbody > tr.row-read > td {
    opacity: 0.5;
  }
`;

const ProcessOwnerCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #EFF6FF;
  border-radius: 50%;
  font-family: Inter;
  font-weight: 600;
  font-size: 12px;
  color: #1E40AF;
  flex-shrink: 0;
`;

const OwnerName = styled.span`
  font-weight: 500;
`;

const QuarterChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #F0FDF4;
  border-radius: 12px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  color: #166534;
`;

const SectionChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #F3F4F6;
  border-radius: 12px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  color: #6B7280;
`;

const QuestionText = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
  max-height: calc(1.5em * 3);
  color: #1F2937;
`;

const ResponseText = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
  max-height: calc(1.5em * 3);
  color: #6B7280;
  cursor: pointer;
  
  &:hover {
    color: #2563EB;
    text-decoration: underline;
  }
`;

const MessageText = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
  max-height: calc(1.5em * 3);
  color: #6B7280;
  cursor: pointer;
  
  &:hover {
    color: #2563EB;
    text-decoration: underline;
  }
`;

// Process Tag style (from FinancialStatementsPage.tsx)
const ProcessTag = styled.span`
  display: inline-block;
  padding: 4px 12px;
  margin: 2px 4px 2px 0;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;

const ProcessTagShort = styled.span`
  display: inline-block;
  padding: 4px 12px;
  margin: 2px 4px 2px 0;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 118px;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;


// Tooltip content styling (from FinancialStatementsPage.tsx)
const TooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #1F2937;
  text-align: center;
  margin-bottom: 12px;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipItem = styled.div`
  padding: 8px 12px;
  background: #EFF6FF;
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #2563EB;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  white-space: normal;
  word-break: break-word;

  &:hover {
    background: #DBEAFE;
    transform: translateX(2px);
  }
`;

const ActionButton = styled.button`
  background: #F8FAFC;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  transition: all 0.3s;

  &:hover {
    color: #1F2937;
    background: #EFF6FF;
  }

  .anticon {
    font-size: 18px;
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MenuItemText = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1F2937;
`;

const MenuItemTextDanger = styled(MenuItemText)`
  color: #DC2626;
`;

const EmptyStateWrapper = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const EmptyStateText = styled.div`
  color: #9CA3AF;
  font-family: Inter;
  font-size: 14px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  background: white;
  border-radius: 6px;
`;

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const DrawerSection = styled.div``;

const DrawerLabel = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  color: #6B7280;
  margin-bottom: 8px;
`;

const DrawerValue = styled.div`
  font-family: Inter;
  font-size: 14px;
  color: #1F2937;
  line-height: 1.6;
`;

// ========== HELPER FUNCTIONS ==========

const getOwnerInitials = (name: string) => {
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// ========== COMPONENT ==========

const ChangeIndicatorChecklistTab: React.FC<ChangeIndicatorChecklistTabProps> = ({ isAdmin }) => {
  // Redux state
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const auditPlanState = useSelector((state: RootState) => state.auditPlan);
  
  // Get data from Redux
  const changeIndicators = (auditPlanState?.changeIndicators || []) as ChangeIndicator[];
  const certificationMessages = (auditPlanState?.certificationMessages || []) as CertificationMessage[];
  const [loadingIndicators, setLoadingIndicators] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  
  // Collapsed state for sections
  const [indicatorsCollapsed, setIndicatorsCollapsed] = useState(false);
  const [messagesCollapsed, setMessagesCollapsed] = useState(false);
  
  // Detail drawer state
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState<ChangeIndicator | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<CertificationMessage | null>(null);
  
  // Delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; type: 'indicator' | 'message' } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Export panel state
  const [isIndicatorExportOpen, setIsIndicatorExportOpen] = useState(false);
  const [isMessageExportOpen, setIsMessageExportOpen] = useState(false);

  // Fetch change indicators
  const fetchChangeIndicators = useCallback(async () => {
    if (!selectedReport?.annualReportId) {
      return;
    }

    setLoadingIndicators(true);
    try {
      const response = await getChangeIndicators({
        annualReportId: selectedReport.annualReportId,
      });
      
      // Dispatch to Redux - component subscribes to get data
      dispatch(setChangeIndicators({
        data: response.data,
        totalCount: response.totalCount,
        reportId: selectedReport.annualReportId,
      }));
    } catch (error: any) {
      console.error('Failed to fetch change indicators:', error);
      message.error(error?.message || 'Failed to load change indicators');
    } finally {
      setLoadingIndicators(false);
    }
  }, [selectedReport?.annualReportId, dispatch]);

  // Fetch certification messages
  const fetchCertificationMessages = useCallback(async () => {
    if (!selectedReport?.annualReportId || !isAdmin) {
      return;
    }

    setLoadingMessages(true);
    try {
      const response = await getCertificationMessages({
        annualReportId: selectedReport.annualReportId,
      });
      
      // Dispatch to Redux - component subscribes to get data
      dispatch(setCertificationMessages({
        data: response.data,
        totalCount: response.totalCount,
        reportId: selectedReport.annualReportId,
      }));
    } catch (error: any) {
      console.error('Failed to fetch certification messages:', error);
      message.error(error?.message || 'Failed to load certification messages');
    } finally {
      setLoadingMessages(false);
    }
  }, [selectedReport?.annualReportId, isAdmin, dispatch]);

  // Fetch data when selectedReport changes
  useEffect(() => {
    fetchChangeIndicators();
    fetchCertificationMessages();
  }, [fetchChangeIndicators, fetchCertificationMessages]);

  // Handle mark as read for Change Indicator
  const handleMarkIndicatorAsRead = useCallback(async (id: string) => {
    try {
      await markChangeIndicatorAsRead(id);
      message.success('Marked as read');
      // Re-fetch to update Redux
      await fetchChangeIndicators();
    } catch (error: any) {
      console.error('Failed to mark as read:', error);
      message.error(error?.message || 'Failed to mark as read');
    }
  }, [fetchChangeIndicators]);

  // Handle mark as read for Certification Message
  const handleMarkMessageAsRead = useCallback(async (id: string) => {
    try {
      await markCertificationMessageAsRead(id);
      message.success('Marked as read');
      // Re-fetch to update Redux
      await fetchCertificationMessages();
    } catch (error: any) {
      console.error('Failed to mark as read:', error);
      message.error(error?.message || 'Failed to mark as read');
    }
  }, [fetchCertificationMessages]);

  // Open delete modal
  const openDeleteModal = useCallback((id: string, type: 'indicator' | 'message') => {
    setItemToDelete({ id, type });
    setDeleteModalOpen(true);
  }, []);

  // Handle delete confirm
  const handleDeleteConfirm = useCallback(async () => {
    if (!itemToDelete) return;

    setDeleting(true);
    try {
      if (itemToDelete.type === 'indicator') {
        await deleteChangeIndicatorApi(itemToDelete.id);
        // Re-fetch to update Redux
        await fetchChangeIndicators();
      } else {
        await deleteCertificationMessageApi(itemToDelete.id);
        // Re-fetch to update Redux
        await fetchCertificationMessages();
      }
      
      message.success('Item deleted successfully');
      setDeleteModalOpen(false);
      setItemToDelete(null);
    } catch (error: any) {
      console.error('Failed to delete item:', error);
      message.error(error?.message || 'Failed to delete item');
    } finally {
      setDeleting(false);
    }
  }, [itemToDelete, fetchChangeIndicators, fetchCertificationMessages]);

  // Open indicator detail
  const openIndicatorDetail = useCallback((indicator: ChangeIndicator) => {
    setSelectedIndicator(indicator);
    setSelectedMessage(null);
    setDetailDrawerOpen(true);
  }, []);

  // Open message detail
  const openMessageDetail = useCallback((msg: CertificationMessage) => {
    setSelectedMessage(msg);
    setSelectedIndicator(null);
    setDetailDrawerOpen(true);
  }, []);

  // Get menu items for Change Indicator
  const getIndicatorMenuItems = (item: ChangeIndicator): MenuProps['items'] => [
    ...(item.status === 'pending' ? [{
      key: 'mark-read',
      label: (
        <MenuItemContent>
          <CheckOutlined />
          <MenuItemText>Mark as Read</MenuItemText>
        </MenuItemContent>
      ),
      onClick: () => handleMarkIndicatorAsRead(item.id),
    }] : []),
    {
      key: 'delete',
      label: (
        <MenuItemContent>
          <DeleteOutlined style={{ color: '#DC2626' }} />
          <MenuItemTextDanger>Delete</MenuItemTextDanger>
        </MenuItemContent>
      ),
      onClick: () => openDeleteModal(item.id, 'indicator'),
    },
  ];

  // Get menu items for Certification Message
  const getMessageMenuItems = (item: CertificationMessage): MenuProps['items'] => [
    ...(item.status === 'pending' ? [{
      key: 'mark-read',
      label: (
        <MenuItemContent>
          <CheckOutlined />
          <MenuItemText>Mark as Read</MenuItemText>
        </MenuItemContent>
      ),
      onClick: () => handleMarkMessageAsRead(item.id),
    }] : []),
    {
      key: 'delete',
      label: (
        <MenuItemContent>
          <DeleteOutlined style={{ color: '#DC2626' }} />
          <MenuItemTextDanger>Delete</MenuItemTextDanger>
        </MenuItemContent>
      ),
      onClick: () => openDeleteModal(item.id, 'message'),
    },
  ];

  // Handle navigation to Process Hierarchy (EX3)
    const handleProcessClick = useCallback((processId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      navigate(`/process-hierarchy?processId=${processId}`);
    }, [navigate]);
  
    // Render linked items with tooltip - show all items on single line, cutoff by cell border
    const renderLinkedItems = (
      items: Array<{ id: string; name: string }>, 
      title: string
    ) => {
      if (items.length === 0) {
        return null;
      }
  
      const handleClick = handleProcessClick;
  
      // Tooltip content for multiple items (styled like FinancialStatementsPage.tsx)
      const tooltipContent = items.length > 0 ? (
        <div>
          <TooltipTitle>{title}</TooltipTitle>
          <TooltipList>
            {items.map((item) => (
              <TooltipItem 
                key={item.id}
                onClick={(e) => handleClick(item.id, e)}
              >
                {item.name}
              </TooltipItem>
            ))}
          </TooltipList>
        </div>
      ) : null;
  
      const content = (
        <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '4px', overflow: 'hidden', maxHeight: '28px', alignItems: 'center' }}>
          {items.map((item) => (
            <ProcessTagShort 
              key={item.id}
              onClick={(e) => handleClick(item.id, e)}
            >
              {item.name}
            </ProcessTagShort>
          ))}
        </div>
      );
  
      if (tooltipContent) {
        return (
          <Tooltip
            title={tooltipContent}
            placement="top"
            overlayStyle={{ maxWidth: '400px' }}
            overlayInnerStyle={{
              backgroundColor: '#FFFFFF',
              padding: '16px',
              borderRadius: '12px',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
            }}
            color="#FFFFFF"
          >
            {content}
          </Tooltip>
        );
      }
  
      return content;
    };

  // Change Indicators columns
  const indicatorColumns: TableColumnsType<ChangeIndicator> = [
    {
      title: 'Quarter',
      dataIndex: 'quarter',
      key: 'quarter',
      width: 80,
      render: (quarter: string) => <QuarterChip>{quarter}</QuarterChip>,
    },
    {
      title: 'Process owner',
      dataIndex: 'processOwnerName',
      key: 'processOwner',
      width: 180,
      render: (name: string) => (
        <ProcessOwnerCell>
          <Avatar>{getOwnerInitials(name)}</Avatar>
          <OwnerName>{name}</OwnerName>
        </ProcessOwnerCell>
      ),
    },
    {
      title: 'Processes',
      dataIndex: 'processes',
      key: 'processes',
      width: 120,
      ellipsis: true,
      onCell: () => ({
        style: { maxWidth: 120, overflow: 'hidden' }
      }),
      render: (processes: Array<{ id: string; name: string }>) => 
        renderLinkedItems(processes, 'Processes'),
    },
    {
      title: 'Question',
      dataIndex: 'question',
      key: 'question',
      width: 450,
      render: (question: string) => (
        <QuestionText>{question}</QuestionText>
      ),
    },
    // {
    //   title: 'Significant change',
    //   dataIndex: 'significantChange',
    //   key: 'significantChange',
    //   width: 160,
    //   render: (status: SignificantChangeValue) => (
    //     <SignificantChangeBadge $status={status}>
    //       {getSignificantChangeLabel(status)}
    //     </SignificantChangeBadge>
    //   ),
    // },
    {
      title: 'Response',
      dataIndex: 'response',
      key: 'response',
      render: (response: string, record: ChangeIndicator) => (
        <ResponseText onClick={() => openIndicatorDetail(record)}>
          {response}
        </ResponseText>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 70,
      align: 'center',
      render: (_: unknown, record: ChangeIndicator) => (
        <Dropdown
          menu={{ items: getIndicatorMenuItems(record) }}
          trigger={['click']}
          placement="bottomRight"
        >
          <ActionButton disabled={!isAdmin} onClick={(e) => e.stopPropagation()}>
            <MoreOutlined />
          </ActionButton>
        </Dropdown>
      ),
    },
  ];

  // Certification Messages columns
  const messageColumns: TableColumnsType<CertificationMessage> = [
    {
      title: 'Quarter',
      dataIndex: 'quarter',
      key: 'quarter',
      width: 80,
      render: (quarter: string) => <QuarterChip>{quarter}</QuarterChip>,
    },
    {
      title: 'Process owner',
      dataIndex: 'processOwnerName',
      key: 'processOwner',
      width: 180,
      render: (name: string) => (
        <ProcessOwnerCell>
          <Avatar>{getOwnerInitials(name)}</Avatar>
          <OwnerName>{name}</OwnerName>
        </ProcessOwnerCell>
      ),
    },
    {
      title: 'Processes',
      dataIndex: 'processes',
      key: 'processes',
      width: 120,
      ellipsis: true,
      onCell: () => ({
        style: { maxWidth: 120, overflow: 'hidden' }
      }),
      render: (processes: Array<{ id: string; name: string }>) => 
        renderLinkedItems(processes, 'Processes'),
    },
    {
      title: 'Section',
      dataIndex: 'section',
      key: 'section',
      width: 150,
      render: (section: string) => (
        <SectionChip>{section}</SectionChip>
      ),
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      render: (msg: string, record: CertificationMessage) => (
        <MessageText onClick={() => openMessageDetail(record)}>
          {msg}
        </MessageText>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 70,
      align: 'center',
      render: (_: unknown, record: CertificationMessage) => (
        <Dropdown
          menu={{ items: getMessageMenuItems(record) }}
          trigger={['click']}
          placement="bottomRight"
        >
          <ActionButton disabled={!isAdmin} onClick={(e) => e.stopPropagation()}>
            <MoreOutlined />
          </ActionButton>
        </Dropdown>
      ),
    },
  ];

  // Sort: Pending first, then by date (newest first)
  const sortedIndicators = [...changeIndicators].sort((a, b) => {
    if (a.status !== b.status) {
      return a.status === 'pending' ? -1 : 1;
    }
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const sortedMessages = [...certificationMessages].sort((a, b) => {
    if (a.status !== b.status) {
      return a.status === 'pending' ? -1 : 1;
    }
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  // Count pending items
  const pendingIndicatorsCount = changeIndicators.filter(i => i.status === 'pending').length;
  const pendingMessagesCount = certificationMessages.filter(m => m.status === 'pending').length;

  // Prepare export data for indicators
  const indicatorExportData = useMemo(() => {
    return sortedIndicators.map((item) => ({
      quarter: item.quarter,
      processOwnerName: item.processOwnerName,
      processes: item.processes.map(p => p.name).join(', ') || '-',
      question: item.question,
      response: item.response,
      status: item.status === 'pending' ? 'Pending' : 'Read',
    }));
  }, [sortedIndicators]);

  // Prepare export data for messages
  const messageExportData = useMemo(() => {
    return sortedMessages.map((item) => ({
      quarter: item.quarter,
      processOwnerName: item.processOwnerName,
      processes: item.processes.map(p => p.name).join(', ') || '-',
      section: item.section,
      message: item.message,
      status: item.status === 'pending' ? 'Pending' : 'Read',
    }));
  }, [sortedMessages]);

  return (
    <Container>
      {/* Change Indicators Section */}
      <Section $bgColor={SECTION_COLORS.changeIndicators}>
        <SectionHeader onClick={() => setIndicatorsCollapsed(!indicatorsCollapsed)}>
          <CollapseIcon>
            {indicatorsCollapsed ? <RightOutlined /> : <DownOutlined />}
          </CollapseIcon>
          <SectionTitle>Change Indicators Responses</SectionTitle>
          {pendingIndicatorsCount > 0 && <CountBadge>{pendingIndicatorsCount} pending</CountBadge>}
          {sortedIndicators.length > 0 && (
            <ExportButton onClick={() => setIsIndicatorExportOpen(true)} />
          )}
        </SectionHeader>

        {!indicatorsCollapsed && (
          <GridWrapper>
            {loadingIndicators ? (
              <LoadingWrapper>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
              </LoadingWrapper>
            ) : (
              <TableWrapper>
                {sortedIndicators.length > 0 ? (
                  <Table
                    dataSource={sortedIndicators}
                    columns={indicatorColumns}
                    rowKey="id"
                    pagination={false}
                    size="middle"
                    rowClassName={(record) => record.status === 'read' ? 'row-read' : ''}
                  />
                ) : (
                  <EmptyStateWrapper>
                    <EmptyStateText>No items to display.</EmptyStateText>
                  </EmptyStateWrapper>
                )}
              </TableWrapper>
            )}
          </GridWrapper>
        )}
      </Section>

      {/* Certification Messages Section - Admin only */}
      {isAdmin && (
        <Section $bgColor={SECTION_COLORS.certificationMessages}>
          <SectionHeader onClick={() => setMessagesCollapsed(!messagesCollapsed)}>
            <CollapseIcon>
              {messagesCollapsed ? <RightOutlined /> : <DownOutlined />}
            </CollapseIcon>
            <SectionTitle>Certification Review Comments</SectionTitle>
            {pendingMessagesCount > 0 && <CountBadge>{pendingMessagesCount} pending</CountBadge>}
            {sortedMessages.length > 0 && (
              <ExportButton onClick={() => setIsMessageExportOpen(true)} />
            )}
          </SectionHeader>

          {!messagesCollapsed && (
            <GridWrapper>
              {loadingMessages ? (
                <LoadingWrapper>
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                </LoadingWrapper>
              ) : (
                <TableWrapper>
                  {sortedMessages.length > 0 ? (
                    <Table
                      dataSource={sortedMessages}
                      columns={messageColumns}
                      rowKey="id"
                      pagination={false}
                      size="middle"
                      rowClassName={(record) => record.status === 'read' ? 'row-read' : ''}
                    />
                  ) : (
                    <EmptyStateWrapper>
                      <EmptyStateText>No items to display.</EmptyStateText>
                    </EmptyStateWrapper>
                  )}
                </TableWrapper>
              )}
            </GridWrapper>
          )}
        </Section>
      )}

      {/* Detail Drawer */}
      <RightContentDrawer
        open={detailDrawerOpen}
        title={selectedIndicator ? 'Change Indicator Detail' : 'Certification Message Detail'}
        onClose={() => {
          setDetailDrawerOpen(false);
          setSelectedIndicator(null);
          setSelectedMessage(null);
        }}
        width={520}
      >
        {selectedIndicator && (
          <DrawerContent>
            <DrawerSection>
              <DrawerLabel>Quarter</DrawerLabel>
              <DrawerValue>
                <QuarterChip>{selectedIndicator.quarter}</QuarterChip>
              </DrawerValue>
            </DrawerSection>
            <DrawerSection>
              <DrawerLabel>Process Owner</DrawerLabel>
              <DrawerValue>
                <ProcessOwnerCell>
                  <Avatar>{getOwnerInitials(selectedIndicator.processOwnerName)}</Avatar>
                  <OwnerName>{selectedIndicator.processOwnerName}</OwnerName>
                </ProcessOwnerCell>
              </DrawerValue>
            </DrawerSection>
            <DrawerSection>
              <DrawerLabel>Processes</DrawerLabel>
              <DrawerValue>
                {selectedIndicator.processes.map((item) => (
                  <ProcessTag 
                    key={item.id}
                    onClick={(e) => handleProcessClick(item.id, e)}
                  >
                    {item.name}
                  </ProcessTag>
                ))}
              </DrawerValue>
            </DrawerSection>
            <DrawerSection>
              <DrawerLabel>Question</DrawerLabel>
              <DrawerValue>{selectedIndicator.question}</DrawerValue>
            </DrawerSection>
            {/* <DrawerSection>
              <DrawerLabel>Significant Change</DrawerLabel>
              <DrawerValue>
                <SignificantChangeBadge $status={selectedIndicator.significantChange}>
                  {getSignificantChangeLabel(selectedIndicator.significantChange)}
                </SignificantChangeBadge>
              </DrawerValue>
            </DrawerSection> */}
            <DrawerSection>
              <DrawerLabel>Response</DrawerLabel>
              <DrawerValue>{selectedIndicator.response}</DrawerValue>
            </DrawerSection>
          </DrawerContent>
        )}

        {selectedMessage && (
          <DrawerContent>
            <DrawerSection>
              <DrawerLabel>Quarter</DrawerLabel>
              <DrawerValue>
                <QuarterChip>{selectedMessage.quarter}</QuarterChip>
              </DrawerValue>
            </DrawerSection>
            <DrawerSection>
              <DrawerLabel>Process Owner</DrawerLabel>
              <DrawerValue>
                <ProcessOwnerCell>
                  <Avatar>{getOwnerInitials(selectedMessage.processOwnerName)}</Avatar>
                  <OwnerName>{selectedMessage.processOwnerName}</OwnerName>
                </ProcessOwnerCell>
              </DrawerValue>
            </DrawerSection>
            <DrawerSection>
              <DrawerLabel>Processes</DrawerLabel>
              <DrawerValue>
                {selectedMessage.processes.map((item) => (
                  <ProcessTag 
                    key={item.id}
                    onClick={(e) => handleProcessClick(item.id, e)}
                  >
                    {item.name}
                  </ProcessTag>
                ))}
              </DrawerValue>
            </DrawerSection>
            <DrawerSection>
              <DrawerLabel>Section</DrawerLabel>
              <DrawerValue>
                <SectionChip>{selectedMessage.section}</SectionChip>
              </DrawerValue>
            </DrawerSection>
            <DrawerSection>
              <DrawerLabel>Message</DrawerLabel>
              <DrawerValue>{selectedMessage.message}</DrawerValue>
            </DrawerSection>
          </DrawerContent>
        )}
      </RightContentDrawer>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        icon={trashIcon}
        title={itemToDelete?.type === 'indicator' ? 'Delete Change Indicator' : 'Delete Message'}
        description="Are you sure you want to delete this item? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        danger={true}
        loading={deleting}
      />

      {/* Export Panel for Change Indicators */}
      <ExportPanel
        open={isIndicatorExportOpen}
        onClose={() => setIsIndicatorExportOpen(false)}
        data={indicatorExportData}
        columns={[
          { key: 'quarter', title: 'Quarter' },
          { key: 'processOwnerName', title: 'Process Owner' },
          { key: 'processes', title: 'Processes' },
          { key: 'question', title: 'Question' },
          { key: 'response', title: 'Response' },
          { key: 'status', title: 'Status' },
        ]}
        fileName="Change_Indicators_Report"
        title="Change Indicators Report"
      />

      {/* Export Panel for Certification Messages */}
      <ExportPanel
        open={isMessageExportOpen}
        onClose={() => setIsMessageExportOpen(false)}
        data={messageExportData}
        columns={[
          { key: 'quarter', title: 'Quarter' },
          { key: 'processOwnerName', title: 'Process Owner' },
          { key: 'section', title: 'Section' },
          { key: 'message', title: 'Message' },
          { key: 'status', title: 'Status' },
        ]}
        fileName="Certification_Messages_Report"
        title="Certification Messages Report"
      />
    </Container>
  );
};

export default ChangeIndicatorChecklistTab;

